import { clsx } from 'clsx';
import type { CSSProperties, MouseEvent } from 'react';

import { spacingCSSVars } from '../../utils/spacing';
import type { AccordionHeadingProps } from './accordion.types';
import { useAutoScroll } from './accordionContext';
import {
  useItemExpanded,
  useItemId,
  useSetItemExpanded,
} from './accordionItemContext';

import styles from './accordion.module.css';

export const AccordionHeading = ({
  as: Component = 'button',
  children,
  className,
  'data-testid': testId,
  disableExpansion = false,
  id,
  onClick,
  style,
  ...props
}: AccordionHeadingProps) => {
  const { autoScrollMargin } = useAutoScroll();
  const expanded = useItemExpanded();
  const setExpanded = useSetItemExpanded();
  const itemId = useItemId();

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    onClick && (onClick as (e: MouseEvent<HTMLElement>) => void)(e);
    !disableExpansion && setExpanded(!expanded);
  };

  const cssVars = {
    '--scroll-margin-top': autoScrollMargin || '0px',
    ...spacingCSSVars(props),
  } as CSSProperties;

  const sharedProps = {
    className: clsx(className, styles['accordionHeading']),
    id: id ?? `accordion-heading-${itemId}`,
    'data-testid': testId ?? `accordion-heading-${itemId}`,
    'aria-controls': `accordion-content-${itemId}`,
    onClick: handleClick,
    onMouseDown: (e: MouseEvent<HTMLElement>) => e.preventDefault(),
    style: { ...cssVars, ...style },
    ...(!disableExpansion ? { 'aria-expanded': expanded } : {}),
  };

  if (Component === 'div') {
    const { as: _as, ...divProps } = props as Record<string, unknown>;
    return (
      <div {...sharedProps} {...divProps}>
        {children}
      </div>
    );
  }

  const { as: _as, ...buttonProps } = props as Record<string, unknown>;
  return (
    <button {...sharedProps} {...buttonProps} type="button">
      {children}
    </button>
  );
};
